import { Card, Button, DatePicker, Input, Tag, Select } from "antd";
import Table, { ColumnsType } from "antd/lib/table";
import moment from "moment-timezone";

import styles from "./index.module.less";
import { TableHelper } from "../../../util/TableHelper";
import { useAdvanceBookingRequest } from "./index.hook";
import Loading from "../../common/Loading";
import { General } from "../../../util/General";
import { ABRStatusConstants, PaymentGatewayConstant } from "../../../constants/DatabaseConstants/ABRConstants";
import AdvanceBookingDetailsModal from "./AdvanceBookingRequestDetailsModal";

import { FormattedAdvanceBookingReportRecord } from "../../../shared/interface/ABRReport.interface";

const AdvanceBookingRequestReport = () => {
  const config = useAdvanceBookingRequest();

  const formatTime = (time: string | null) => (time ? moment(time).format("HH:mm") : "");

  const renderTime = (key: keyof FormattedAdvanceBookingReportRecord) => (text: any) => <div>{formatTime(text)}</div>;
  const valueSorter = (key: keyof FormattedAdvanceBookingReportRecord) => ({ compare: TableHelper.valueCompare(key) });

  const columns: ColumnsType<FormattedAdvanceBookingReportRecord> = [
    {
      align: "right",
      title: "Sr No.",
      dataIndex: "sr_no",
      key: "sr_no",
      render: TableHelper.renderNumberValue,
      sorter: valueSorter("sr_no")
    },
    {
      align: "left",
      title: "Booking ID",
      dataIndex: "booking_id",
      key: "booking_id",
      render: (text, record) => (
        <div onClick={() => config.handleModalOpen(record?.id)} className={`gx-link ${record.id === config.selectedRecordId ? styles["modal__description"] : ""}`}>
          {TableHelper.getValue(text)}
        </div>
      ),
      sorter: valueSorter("booking_id")
    },
    {
      align: "left",
      title: "Username",
      dataIndex: "full_name",
      key: "full_name",
      render: TableHelper.renderText,
      sorter: valueSorter("full_name")
    },
    {
      align: "left",
      title: "Vehicle Type",
      dataIndex: "vehicle_type_name",
      key: "vehicle_type_name",
      render: TableHelper.renderText,
      sorter: valueSorter("vehicle_type_name")
    },
    {
      title: "Booking Time",
      children: [
        {
          align: "right",
          title: "In-Time",
          dataIndex: "parking_start_date",
          key: "parking_start_date",
          render: renderTime("parking_start_date"),
          sorter: { compare: TableHelper.dateSorter("parking_start_date") }
        },
        {
          align: "right",
          title: "Out-Time",
          dataIndex: "parking_end_date",
          key: "parking_end_date",
          render: renderTime("parking_end_date"),
          sorter: { compare: TableHelper.dateSorter("parking_end_date") }
        }
      ]
    },
    {
      title: "Parking Time",
      children: [
        {
          align: "right",
          title: "In-Time",
          dataIndex: "parking_in_time",
          key: "parking_in_time",
          render: renderTime("parking_in_time"),
          sorter: { compare: TableHelper.dateSorter("parking_in_time") }
        },
        {
          align: "right",
          title: "Out-Time",
          dataIndex: "parking_out_time",
          key: "parking_out_time",
          render: renderTime("parking_out_time"),
          sorter: { compare: TableHelper.dateSorter("parking_out_time") }
        }
      ]
    },
    {
      align: "right",
      title: "Parking Amount",
      dataIndex: "parking_amount",
      key: "parking_amount",
      render: (text) => <div>{text} ₹</div>,
      sorter: valueSorter("parking_amount")
    },
    {
      align: "right",
      title: "Valet Amount",
      dataIndex: "valet_parking_amount",
      key: "valet_parking_amount",
      render: (text) => <div>{text} ₹</div>,
      sorter: valueSorter("valet_parking_amount")
    },
    {
      align: "right",
      title: "Overdue Amount",
      dataIndex: "overdue_amount",
      key: "overdue_amount",
      render: (text) => <div>{text} ₹</div>,
      sorter: valueSorter("overdue_amount")
    },
    {
      align: "right",
      title: "Penalty Amount",
      dataIndex: "penalty_amount",
      key: "penalty_amount",
      render: (text) => <div>{text} ₹</div>,
      sorter: valueSorter("penalty_amount")
    },
    {
      align: "right",
      title: "Payable Amount",
      dataIndex: "payable_amount",
      key: "payable_amount",
      render: (text) => <div>{text} ₹</div>,
      sorter: valueSorter("payable_amount")
    },
    {
      align: "left",
      title: "Payment",
      dataIndex: "payment_stage",
      key: "payment_stage",
      render: (text) => <div>{text}</div>,
      sorter: valueSorter("payment_stage")
    },
    {
      align: "left",
      title: "Request Status",
      sorter: (a, b) => (a.is_completed === false ? -1 : 1),
      render: (text, record) => <div className="table-text-wrap">{record?.is_completed === true ? <Tag color="success">{ABRStatusConstants.COMPLETED}</Tag> : <Tag color="error">{ABRStatusConstants.CANCELLED}</Tag>}</div>
    }
  ];

  return (
    <>
      <Card className="gx-card card--height" title={null}>
        <div className={styles["head-container"]}>
          <div className={styles["abr-penalty-report-left-section"]}>
            <span className="card-title-style gx-mr-3">ADVANCE BOOKING REPORT</span>
            <Button onClick={config.handleRefreshList} className={`button-gradiant gx-mb-0 add-button`} loading={config.loading}>
              Refresh
            </Button>
          </div>
          <div className={styles["right-section-filter"]}>
            <div className={styles["right-section-filter-top"]}>
              <Select showSearch allowClear value={config?.selectedPaymentGateway} listHeight={200} style={{ width: "220px" }} onChange={(value) => config?.handleChangePaymentGateway(value)} getPopupContainer={(trigger) => trigger.parentElement} filterOption={General.searchFilterOnSelect} className={styles["dropdown__selector_small"]} placeholder="Select Payment Gateway">
                <Select.Option value={PaymentGatewayConstant.BILLDESK}>{PaymentGatewayConstant.BILLDESK.toUpperCase()}</Select.Option>
                <Select.Option value={PaymentGatewayConstant.RAZORPAY}>{PaymentGatewayConstant.RAZORPAY.toUpperCase()}</Select.Option>
                <Select.Option value={PaymentGatewayConstant.OFFLINE}>{PaymentGatewayConstant.OFFLINE.toUpperCase()}</Select.Option>
              </Select>

              <Select showSearch value={config?.selectedCompletionStatus} listHeight={200} style={{ width: "200px" }} onChange={(value) => config?.handleChangeStatusFilter(value)} getPopupContainer={(trigger) => trigger.parentElement} filterOption={General.searchFilterOnSelect} className={styles["dropdown__selector_small"]} placeholder="Select Request Status">
                <Select.Option value={ABRStatusConstants.COMPLETED}>{ABRStatusConstants.COMPLETED}</Select.Option>
                <Select.Option value={ABRStatusConstants.CANCELLED}>{ABRStatusConstants.CANCELLED}</Select.Option>
                <Select.Option value={ABRStatusConstants.NO_SHOW}>{ABRStatusConstants.NO_SHOW}</Select.Option>
              </Select>
              <DatePicker format="DD-MM-YYYY" defaultValue={moment()} onChange={config.handleDateChange} getPopupContainer={(trigger) => trigger.parentElement || document.body} disabledDate={General.disableFutureDate} />
            </div>
            <Input.Search className="gx-mb-0 search-component__width" allowClear placeholder="Search..." value={config.searchedText} onChange={(e) => config.handleChangeOnSearch(e.target.value)} onSearch={config.handleChangeSearch} />
          </div>
        </div>
        {config.loading ? <Loading /> : <Table className="gx-table-responsive gx-mt-4" size="small" columns={columns} dataSource={config.abrList ?? []} pagination={config.pagination} bordered onChange={config.handleChangePagination} rowKey={(record) => record.id} />}
      </Card>

      <AdvanceBookingDetailsModal handleOnClose={config.handleOnClose} isModalVisible={config.isModalVisible} abrRecordId={config.selectedRecordId} />
    </>
  );
};

export default AdvanceBookingRequestReport;
